<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useForm, usePage } from '@inertiajs/vue3';
import { ClockIcon as ClockIconMini } from '@heroicons/vue/20/solid';
import { MagnifyingGlassIcon, ArrowRightIcon, ArrowLeftIcon, HomeIcon, BuildingOffice2Icon, SunIcon, FireIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { getFunnelVariant } from '@/lib/funnelVariants';
import ConsentNotice from '@/Components/ConsentNotice.vue';

let _trackEvent = () => {};
let _fireConversionEvents = () => {};
import('@/lib/tracking').then(m => { _trackEvent = m.trackEvent; _fireConversionEvents = m.fireConversionEvents; });

let _parsePhone = null;
let _isEmail = null;
const preloadValidators = () => {
    import('libphonenumber-js/min').then(m => { _parsePhone = m.parsePhoneNumberFromString; });
    import('validator/lib/isEmail').then(m => { _isEmail = m.default; });
};

const props = defineProps({
    domain: Object,
    service: String,
    city: Object,
    location: Object,
    compact: Boolean,
    bare: Boolean,
    darkMode: Boolean,
    designVariant: Object,
    funnelVariant: Object,
    nearbyPartnerCount: { type: Number, default: 0 },
    todayLeadCount: { type: Number, default: 0 },
    averageRating: { type: String, default: '4.9' },
    promotion: { type: Object, default: null },
    forceFs: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);

const c = computed(() => {
    // Honor the darkMode prop: when the funnel is embedded directly on a dark
    // section (e.g. a navy CTA band) WITHOUT a white card behind it, dark text
    // is invisible. Callers that place the funnel on a dark background pass
    // :dark-mode="true" so headings/labels switch to light. The default stays
    // light (dark text on the white funnel card).
    const d = props.darkMode;
    return {
        heading: d ? '#fff' : '#1a1a1a',
        sub: d ? 'rgba(255,255,255,0.7)' : '#555555',
        muted: d ? 'rgba(255,255,255,0.7)' : '#737373',
        label: d ? 'rgba(255,255,255,0.85)' : '#334155',
        inputBg: d ? 'rgba(255,255,255,0.08)' : '#fafafa',
        inputBorder: d ? 'rgba(255,255,255,0.18)' : '#d4d4d4',
        inputColor: d ? '#fff' : '#1a1a1a',
        stepBg: d ? 'rgba(255,255,255,0.1)' : '#f1f1f1',
        stepColor: d ? 'rgba(255,255,255,0.7)' : '#737373',
        trustBg: d ? 'rgba(255,255,255,0.08)' : '#f8fafc',
        trustBorder: d ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.08)',
        trustColor: d ? 'rgba(255,255,255,0.8)' : '#475569',
        optText: d ? 'rgba(255,255,255,0.7)' : '#64748b',
        optSelected: d ? '#fff' : '#333',
        optUnselected: d ? 'rgba(255,255,255,0.7)' : '#64748b',
        progressBg: d ? 'rgba(255,255,255,0.1)' : '#f1f1f1',
        footerBorder: d ? 'rgba(255,255,255,0.1)' : '#f1f1f1',
        footerHeading: d ? 'rgba(255,255,255,0.9)' : '#333',
        footerSub: d ? 'rgba(255,255,255,0.7)' : '#737373',
        // Eyebrow/accent label. On the white card use the -fg variant
        // (darker, on-white-tuned). On a DARK embed we can't trust the raw
        // brand accent - some domains use a dark slate accent (e.g. #334155)
        // that is itself invisible on the dark panel. Mix the accent 55%
        // toward white so it stays brand-tinted but always readable on dark.
        eyebrow: d
            ? 'color-mix(in srgb, var(--brand-accent) 45%, #ffffff)'
            : 'var(--brand-accent-fg, var(--brand-accent))',
        savingsBg: d ? 'rgba(34,197,94,0.12)' : '#f0fdf4',
        savingsBorder: d ? 'rgba(34,197,94,0.3)' : '#bbf7d0',
        savingsColor: d ? '#4ade80' : '#166534',
        urgentBg: d ? 'rgba(251,191,36,0.1)' : '#fefce8',
        urgentBorder: d ? 'rgba(251,191,36,0.25)' : '#fde68a',
        urgentColor: d ? '#fbbf24' : '#92400e',
        urgentIcon: d ? '#fbbf24' : '#d97706',
        privacyBg: d ? 'rgba(255,255,255,0.05)' : '#f8fafc',
        privacyBorder: d ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.08)',
        privacyColor: d ? 'rgba(255,255,255,0.7)' : '#475569',
    };
});

const page = usePage();

const resolvedService = computed(() => props.service || page.props.service || 'wp');
const isWP = computed(() => resolvedService.value === 'wp');
const activeLocation = computed(() => props.location || props.city || page.props.location || page.props.city || null);
const activeDesignVariant = computed(() => props.designVariant || page.props.designVariant || null);
const requestedFunnelVariant = computed(() => props.funnelVariant || page.props.funnelVariant || null);
const activeFunnel = computed(() => {
    const baseVariant = getFunnelVariant(requestedFunnelVariant.value?.id || 'guided-standard');
    if (!resolvedService.value) {
        return {
            ...baseVariant,
            steps: [
                { key: 'service_selection', label: 'Interesse' },
                ...baseVariant.steps
            ]
        };
    }
    return baseVariant;
});

const step = ref(1);
const forceFs = ref((() => {
    if (props.forceFs) return true;
    if (typeof window === 'undefined') return false;
    const p = new URLSearchParams(window.location.search);
    return p.get('funnel') === 'open' || p.get('fs') === '1';
})());
watch(() => props.forceFs, (v) => { forceFs.value = !!v; });
const isFs = computed(() => step.value > 1 || forceFs.value);
const stepDirection = ref('forward');
const storageKey = ref('');
const plzError = ref('');
const stepError = ref('');
const submitError = ref('');
const nameError = ref('');
const phoneError = ref('');
const emailError = ref('');
const trackingReady = ref(false);
const disqualified = ref(false);

const partialLeadId = ref(null);
const partialToken = ref('');

const form = useForm({
    postal_code: '',
    service_type: resolvedService.value,
    property_type: '',
    monthly_bill: '',
    ownership_status: '',
    installation_timeline: '',
    project_goal: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    street: '',
    house_number: '',
    partial_lead_id: null,
    partial_token: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    landing_page: '',
    referrer: '',
    location_id: props.location?.id || props.city?.id || null,
    location_slug: props.location?.slug || props.city?.slug || '',
    location_name: props.location?.name || props.city?.name || '',
    promotion_discount: props.promotion?.discount || null,
    design_variant_id: null,
    design_variant_family: '',
    design_variant_name: '',
    funnel_variant_id: '',
    funnel_variant_family: '',
    funnel_variant_name: '',
    // With button-as-consent (wording rendered immediately above each submit
    // button), the user's click is itself the affirmative action — no checkbox
    // is drawn any more, so both flags default to true and are always sent.
    privacy_accepted: true,
    consent_acknowledged: true,
    // UWG § 7 separate phone-advertising opt-in. UNticked by default; governs
    // whether we may call/SMS. The lead is submitted regardless of this box.
    phone_advertising_consent: false,
});

const totalSteps = computed(() => activeFunnel.value.steps.length);
const currentStep = computed(() => activeFunnel.value.steps[step.value - 1] || activeFunnel.value.steps[0]);
const progress = computed(() => (step.value / totalSteps.value) * 100);
const stepLabels = computed(() => activeFunnel.value.steps.map((item) => item.label));
const trustPoints = computed(() => [
    'Kostenlos & unverbindlich',
    `${props.nearbyPartnerCount || (isWP.value ? 24 : 18)}+ Fachbetriebe`,
    'Antwort in 24h',
]);

const stepsRemaining = computed(() => totalSteps.value - step.value);
const encouragementText = computed(() => {
    if (stepsRemaining.value === 0) return 'Letzter Schritt!';
    if (stepsRemaining.value === 1) return 'Nur noch 1 Schritt!';
    if (stepsRemaining.value === 2) return 'Nur noch 2 Schritte!';
    return null;
});

const estimatedSavings = computed(() => {
    const bill = Number(form.monthly_bill || 0);
    const pct = isWP.value ? 0.45 : 0.35;
    return Math.round((bill * pct * 12) / 10) * 10;
});

const includesStep = (key) => activeFunnel.value.steps.some((item) => item.key === key);

const syncExperimentFields = () => {
    form.service_type = resolvedService.value;
    form.location_id = activeLocation.value?.id || null;
    form.location_slug = activeLocation.value?.slug || '';
    form.location_name = activeLocation.value?.name || '';
    form.design_variant_id = activeDesignVariant.value?.id || null;
    form.design_variant_family = activeDesignVariant.value?.family || '';
    form.design_variant_name = activeDesignVariant.value?.name || '';
    form.funnel_variant_id = activeFunnel.value.id;
    form.funnel_variant_family = activeFunnel.value.family;
    form.funnel_variant_name = activeFunnel.value.name;

    if (includesStep('costs') && !form.monthly_bill) {
        form.monthly_bill = '150';
    }

    if (!includesStep('costs')) {
        form.monthly_bill = '';
    }

    if (!includesStep('property')) {
        form.property_type = '';
    }

    if (includesStep('qualification') && !form.project_goal) {
        form.project_goal = 'comparison';
    }

    if (!includesStep('qualification')) {
        form.ownership_status = '';
        form.installation_timeline = '';
        form.project_goal = '';
    }
};

const experimentPayload = () => ({
    service_type: form.service_type,
    city_slug: activeLocation.value?.slug,
    location_id: activeLocation.value?.id,
    location_slug: activeLocation.value?.slug,
    location_name: activeLocation.value?.name,
    design_variant_id: form.design_variant_id,
    design_variant_family: form.design_variant_family,
    design_variant_name: form.design_variant_name,
    funnel_variant_id: form.funnel_variant_id,
    funnel_variant_family: form.funnel_variant_family,
    funnel_variant_name: form.funnel_variant_name,
});

const trackStepView = () => {
    _trackEvent('funnel_step_view', {
        event_category: 'funnel',
        ...experimentPayload(),
        value: step.value,
        metadata: {
            compact: !!props.compact,
            step_key: currentStep.value.key,
            step_label: currentStep.value.label,
            step_count: totalSteps.value,
        },
    });
};

const validatePLZ = () => {
    const plz = form.postal_code.replace(/\D/g, '');

    if (plz.length !== 5) {
        plzError.value = 'Bitte geben Sie eine gültige 5-stellige PLZ ein.';
        nextTick(() => plzInputRef.value?.focus?.());
        return false;
    }

    form.postal_code = plz;
    plzError.value = '';
    return true;
};

const validatePhone = () => {
    if (!form.phone?.trim()) {
        return true;
    }

    if (_parsePhone) {
        const parsedPhone = _parsePhone(form.phone, 'DE');
        if (parsedPhone?.isValid()) {
            form.phone = parsedPhone.formatNational();
            return true;
        }
    }

    const digits = form.phone.replace(/\D/g, '');

    if (digits.length < 7) {
        submitError.value = 'Bitte geben Sie eine gültige Telefonnummer ein.';
        return false;
    }

    return true;
};

// Exit confirmation. Once the user has invested effort (past step 1, or filled
// any meaningful field), closing must require explicit confirmation. Paid
// traffic that bounces from the funnel because of a misclick is wasted spend.
const confirmExitOpen = ref(false);

const funnelHasProgress = computed(() => {
    if (step.value > 1) return true;
    if (form.postal_code) return true;
    if (form.first_name || form.last_name) return true;
    if (form.phone || form.email) return true;
    if (form.property_type || form.monthly_bill) return true;
    if (form.ownership_status || form.installation_timeline || form.project_goal) return true;
    return false;
});

const performClose = () => {
    document.body.style.overflow = '';
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('fs');
        url.searchParams.delete('funnel');
        window.history.replaceState({}, '', url.toString());
    } catch {}

    confirmExitOpen.value = false;

    // Parent-driven (v-if + @close): just notify, let parent unmount us.
    // Mutating forceFs/step here causes a :key swap to race with the parent
    // unmount in the same flush, crashing Vue with "null.nextSibling".
    if (props.forceFs) {
        emit('close');
        return;
    }

    // Self-driven (URL ?fs=1, no parent v-if): reset local state so the
    // shell switches back to inline rendering.
    forceFs.value = false;
    step.value = 1;
    emit('close');
};

const closeFullscreen = () => {
    if (funnelHasProgress.value) {
        confirmExitOpen.value = true;
        return;
    }
    performClose();
};

const cancelExit = () => {
    confirmExitOpen.value = false;
};

const onOverlayClick = (e) => {
    if (e.target === e.currentTarget) closeFullscreen();
};

const onEscKey = (e) => {
    if (e.key !== 'Escape') return;
    if (confirmExitOpen.value) {
        cancelExit();
        return;
    }
    if (isFs.value) closeFullscreen();
};

const hasService = (svc) => {
    const t = form.service_type;
    if (svc === 'pv') return t === 'pv' || t === 'kombi';
    if (svc === 'wp') return t === 'wp' || t === 'kombi';
    return false;
};
const toggleService = (svc) => {
    const has = hasService(svc);
    const other = svc === 'pv' ? 'wp' : 'pv';
    const hasOther = hasService(other);
    if (has) {
        form.service_type = hasOther ? other : '';
    } else {
        form.service_type = hasOther ? 'kombi' : svc;
    }
};

// Move focus to the first invalid field after a failed validation. Honors
// the WCAG focus-management guidance — users (especially screen-reader
// and keyboard users) need to be carried to the field they must fix.
const focusInvalid = (refValue) => {
    nextTick(() => {
        const el = refValue?.value;
        if (el && typeof el.focus === 'function') el.focus();
    });
};

const validateCurrentStep = () => {
    stepError.value = '';
    nameError.value = '';
    phoneError.value = '';

    switch (currentStep.value.key) {
        case 'location':
            return validatePLZ();
        case 'phone':
            // Phone-only final step (2026-05-30): the 3-field name+phone wall at
            // peak commitment was the highest-friction screen. Name is now
            // optional and enriched on the Success page. Validate phone only.
            if (!form.phone?.trim()) {
                phoneError.value = 'Bitte geben Sie Ihre Telefonnummer ein.';
                stepError.value = 'Bitte geben Sie Ihre Telefonnummer ein.';
                focusInvalid(phoneRef);
                return false;
            }
            if (!validatePhone()) {
                phoneError.value = submitError.value;
                stepError.value = submitError.value;
                submitError.value = '';
                focusInvalid(phoneRef);
                return false;
            }
            return true;
        case 'name':
            if (!form.first_name?.trim() || !form.last_name?.trim()) {
                nameError.value = 'Bitte geben Sie Vor- und Nachname ein.';
                stepError.value = 'Bitte geben Sie Vor- und Nachname ein.';
                focusInvalid(firstNameRef);
                return false;
            }
            return true;
        case 'address':
            // Optional: only validate when at least one field is filled.
            if ((form.street?.trim() || form.house_number?.trim())
                && (!form.street?.trim() || !form.house_number?.trim())) {
                stepError.value = 'Bitte geben Sie Straße und Hausnummer ein.';
                focusInvalid(streetRef);
                return false;
            }
            return true;
        case 'email':
            // Optional: only validate when filled.
            if (form.email?.trim()) {
                const valid = _isEmail
                    ? _isEmail(form.email.trim())
                    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
                if (!valid) {
                    emailError.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
                    stepError.value = emailError.value;
                    return false;
                }
            }
            return true;
        case 'property':
        case 'property_filter':
            if (!form.property_type) {
                stepError.value = 'Bitte wählen Sie Ihren Immobilientyp.';
                return false;
            }
            return true;
        case 'service_selection':
            if (!form.service_type) {
                stepError.value = 'Bitte wählen Sie aus, wofür Sie sich interessieren.';
                return false;
            }
            return true;
        case 'qualification':
            if (!form.ownership_status || !form.installation_timeline || !form.project_goal) {
                stepError.value = 'Bitte beantworten Sie die drei Projektfragen.';
                return false;
            }
            return true;
        default:
            return true;
    }
};

const persistState = () => ({
    step: step.value,
    partialLeadId: partialLeadId.value,
    partialToken: partialToken.value,
    form: {
        postal_code: form.postal_code,
        phone: form.phone,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        street: form.street,
        house_number: form.house_number,
        property_type: form.property_type,
        monthly_bill: form.monthly_bill,
        ownership_status: form.ownership_status,
        installation_timeline: form.installation_timeline,
        project_goal: form.project_goal,
        privacy_accepted: form.privacy_accepted,
    },
});

const plzInputRef = ref(null);
const phoneRef = ref(null);
const firstNameRef = ref(null);
const streetRef = ref(null);
const emailRef = ref(null);
const privacyRef = ref(null);

const focusCurrentInput = () => {
    nextTick(() => {
        if (currentStep.value.key === 'location' && plzInputRef.value) {
            plzInputRef.value.focus();
        } else if (currentStep.value.key === 'phone' && phoneRef.value) {
            phoneRef.value.focus();
        } else if (currentStep.value.key === 'name' && firstNameRef.value) {
            firstNameRef.value.focus();
        } else if (currentStep.value.key === 'address' && streetRef.value) {
            streetRef.value.focus();
        } else if (currentStep.value.key === 'email' && emailRef.value) {
            emailRef.value.focus();
        }
    });
};

// Enrichment calls that fire before the partial lead exists (possible with
// funnel variants that collect name or street before phone) land here and
// are replayed once createPartialLead returns. Otherwise those fields would
// be silently dropped.
let pendingEnrichments = {};

const createPartialLead = async () => {
    syncExperimentFields();
    try {
        const res = await fetch('/leads/partial', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
                postal_code: form.postal_code,
                // Nullable: the partial is created as early as the PLZ step
                // (before phone) so abandoned/failing funnels still leave a row.
                phone: form.phone || null,
                service_type: form.service_type,
                // Any fields the user already filled upstream of the phone
                // step — sent at creation so they aren't lost.
                first_name: form.first_name || null,
                last_name: form.last_name || null,
                email: form.email || null,
                street: form.street || null,
                house_number: form.house_number || null,
                design_variant_id: form.design_variant_id,
                design_variant_family: form.design_variant_family,
                design_variant_name: form.design_variant_name,
                funnel_variant_id: form.funnel_variant_id,
                funnel_variant_family: form.funnel_variant_family,
                funnel_variant_name: form.funnel_variant_name,
                consent_acknowledged: true,
                // UWG § 7 separate opt-in: only when ticked does the server
                // record phone/sms call-consent for this partial lead.
                phone_advertising_consent: form.phone_advertising_consent,
            }),
        });
        if (res.ok) {
            const data = await res.json();
            partialLeadId.value = data.lead_id;
            partialToken.value = data.token;

            // Flush anything that tried to enrich before the lead existed.
            if (Object.keys(pendingEnrichments).length) {
                const flush = pendingEnrichments;
                pendingEnrichments = {};
                enrichLead(flush);
            }
        }
    } catch { /* silent - final submit is the fallback */ }
};

const enrichLead = async (fields) => {
    if (!partialLeadId.value) {
        // Buffer until createPartialLead resolves so no field is lost.
        pendingEnrichments = { ...pendingEnrichments, ...fields };
        return;
    }
    try {
        await fetch(`/leads/${partialLeadId.value}/enrich`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ ...fields, token: partialToken.value }),
        });
    } catch { /* silent */ }
};

const nextStep = () => {
    submitError.value = '';

    if (!validateCurrentStep()) {
        return;
    }

    if (step.value < totalSteps.value) {
        const fromStep = currentStep.value;
        stepDirection.value = 'forward';

        // Progressive save based on completed step
        if (fromStep.key === 'location') {
            // Create the partial the moment a valid PLZ is entered so a
            // tracked row exists even if the visitor abandons or a later step
            // fails. Phone is null at this point and enriched later.
            if (partialLeadId.value) {
                enrichLead({ postal_code: form.postal_code });
            } else {
                createPartialLead();
            }
        } else if (fromStep.key === 'phone') {
            if (partialLeadId.value) {
                enrichLead({ phone: form.phone });
            } else {
                createPartialLead();
            }
        } else if (fromStep.key === 'name') {
            enrichLead({ first_name: form.first_name, last_name: form.last_name });
        } else if (fromStep.key === 'address' && form.street?.trim() && form.house_number?.trim()) {
            enrichLead({ street: form.street, house_number: form.house_number });
        } else if (fromStep.key === 'property') {
            enrichLead({ property_type: form.property_type });
        } else if (fromStep.key === 'qualification') {
            enrichLead({ ownership_status: form.ownership_status, installation_timeline: form.installation_timeline, project_goal: form.project_goal });
        } else if (fromStep.key === 'costs') {
            enrichLead({ monthly_bill: form.monthly_bill });
        }

        step.value++;
        focusCurrentInput();

        _trackEvent('funnel_step_next', {
            event_category: 'funnel',
            ...experimentPayload(),
            value: step.value,
            metadata: {
                compact: !!props.compact,
                from_step: fromStep.key,
                to_step: currentStep.value.key,
                step_count: totalSteps.value,
            },
        });
    }
};

const skipOptional = () => {
    submitError.value = '';
    stepError.value = '';
    emailError.value = '';
    if (currentStep.value.key === 'address') {
        form.street = '';
        form.house_number = '';
    } else if (currentStep.value.key === 'email') {
        form.email = '';
    }
    if (step.value < totalSteps.value) {
        const fromStep = currentStep.value;
        stepDirection.value = 'forward';
        step.value++;
        focusCurrentInput();
        _trackEvent('funnel_step_skip', {
            event_category: 'funnel',
            ...experimentPayload(),
            value: step.value,
            metadata: {
                from_step: fromStep.key,
                to_step: currentStep.value.key,
                step_count: totalSteps.value,
            },
        });
    }
};

const prevStep = () => {
    if (step.value <= 1) {
        if (isFs.value) closeFullscreen();
        return;
    }
    if (step.value > 1) {
        const fromStep = currentStep.value;
        stepDirection.value = 'back';
        step.value--;

        _trackEvent('funnel_step_back', {
            event_category: 'funnel',
            ...experimentPayload(),
            value: step.value,
            metadata: {
                from_step: fromStep.key,
                to_step: currentStep.value.key,
                step_count: totalSteps.value,
            },
        });
    }
};

const submit = () => {
    submitError.value = '';
    stepError.value = '';
    phoneError.value = '';
    syncExperimentFields();

    // Phone-only final step (2026-05-30): name is OPTIONAL and enriched
    // post-submit on the Success page. Do NOT block submit on a missing name
    // (this guard was the regression that blocked every Vue-funnel lead).
    if (!form.phone?.trim()) {
        phoneError.value = 'Bitte geben Sie Ihre Telefonnummer ein.';
        submitError.value = phoneError.value;
        focusInvalid(phoneRef);
        return;
    }
    if (!validatePhone()) {
        phoneError.value = submitError.value;
        focusInvalid(phoneRef);
        return;
    }

    if (!form.privacy_accepted) {
        submitError.value = 'Bitte bestätigen Sie die Datenschutzerklärung, AGB und die Einwilligung zur Datenweitergabe.';
        nextTick(() => privacyRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
        return;
    }

    // Resilience: push the phone onto the existing PLZ-partial NOW, so it is
    // captured even if the final submit below fails for any reason.
    if (partialLeadId.value && form.phone) {
        enrichLead({ phone: form.phone });
    }

    form.partial_lead_id = partialLeadId.value;
    form.partial_token = partialToken.value;

    form.post('/leads', {
        onSuccess: () => {
            if (storageKey.value) {
                window.sessionStorage.removeItem(storageKey.value);
            }
            // Conversion firing (fireConversionEvents + lead_submit_success) is
            // owned solely by Success.vue onMounted - the stable post-navigation
            // page with no funnel-teardown race. Firing here too caused the
            // double-fire + sendBeacon-drop that made lead_submit_success
            // unreliable (~75% loss; 91 events vs 22 rows).
        },
        onError: (errors) => {
            const firstError = Object.values(errors || {})[0];
            if (firstError) {
                submitError.value = Array.isArray(firstError) ? firstError[0] : firstError;
            } else {
                submitError.value = 'Es gab ein Problem. Bitte versuchen Sie es erneut.';
            }
            _trackEvent('lead_submit_error', {
                event_category: 'conversion',
                ...experimentPayload(),
                metadata: {
                    step_key: currentStep.value.key,
                    step_count: totalSteps.value,
                },
            });
        },
    });
};

onMounted(() => {
    preloadValidators();
    const params = new URLSearchParams(window.location.search);

    if (forceFs.value) {
        document.body.style.overflow = 'hidden';
        nextTick(() => { if (!inheritedCssVars.value) captureAccentColor(); });
    }

    syncExperimentFields();
    storageKey.value = `lead-funnel:${props.domain?.request_host || window.location.hostname}:${resolvedService.value}:${activeFunnel.value.id}`;

    const savedState = storageKey.value ? window.sessionStorage.getItem(storageKey.value) : null;

    if (savedState) {
        try {
            const parsedState = JSON.parse(savedState);
            Object.assign(form, parsedState.form || {});
            step.value = parsedState.step || 1;
            partialLeadId.value = parsedState.partialLeadId || null;
            partialToken.value = parsedState.partialToken || '';
        } catch {
            window.sessionStorage.removeItem(storageKey.value);
        }
    }

    if (step.value > totalSteps.value) {
        step.value = totalSteps.value;
    }

    form.utm_source = params.get('utm_source') || '';
    form.utm_medium = params.get('utm_medium') || '';
    form.utm_campaign = params.get('utm_campaign') || '';
    form.utm_term = params.get('utm_term') || '';
    form.utm_content = params.get('utm_content') || '';
    form.landing_page = window.location.href;
    form.referrer = document.referrer || '';
    syncExperimentFields();

    _trackEvent('funnel_view', {
        event_category: 'funnel',
        ...experimentPayload(),
        value: totalSteps.value,
        metadata: {
            compact: !!props.compact,
            step: step.value,
            step_count: totalSteps.value,
            intro: activeFunnel.value.intro,
        },
    });

    trackingReady.value = true;
    trackStepView();
    focusCurrentInput();
});

onMounted(() => { window.addEventListener('keydown', onEscKey); });
onUnmounted(() => {
    document.body.style.overflow = '';
    window.removeEventListener('keydown', onEscKey);
});

watch(activeFunnel, () => {
    syncExperimentFields();

    if (step.value > totalSteps.value) {
        step.value = totalSteps.value;
    }
});

watch(
    () => persistState(),
    (value) => {
        if (!storageKey.value) {
            return;
        }

        window.sessionStorage.setItem(storageKey.value, JSON.stringify(value));
    },
    { deep: true }
);

// Capture CSS custom properties so we can re-apply them on the Teleported container
const shellRef = ref(null);
const inheritedCssVars = ref('');

const captureAccentColor = () => {
    const el = shellRef.value;
    const root = document.documentElement;
    const accent = (el && getComputedStyle(el).getPropertyValue('--brand-accent').trim())
        || getComputedStyle(root).getPropertyValue('--brand-accent').trim()
        || 'var(--brand-accent)';
    const accentSoft = (el && getComputedStyle(el).getPropertyValue('--brand-accent-soft').trim())
        || getComputedStyle(root).getPropertyValue('--brand-accent-soft').trim()
        || (accent + '24');
    inheritedCssVars.value = `--brand-accent:${accent};--brand-accent-soft:${accentSoft};`;
};

onMounted(() => { nextTick(captureAccentColor); });

// Auto-advance when all qualification questions are answered
watch(
    () => [form.ownership_status, form.installation_timeline, form.project_goal],
    ([os, it, pg]) => {
        if (currentStep.value.key === 'qualification' && os && it && pg) {
            setTimeout(() => {
                stepDirection.value = 'forward';
                nextStep();
            }, 350);
        }
    }
);

watch(step, (newStep, oldStep) => {
    plzError.value = '';
    stepError.value = '';

    // Lock/unlock body scroll for fullscreen funnel
    if (newStep > 1 && oldStep <= 1) {
        document.body.style.overflow = 'hidden';
        if (!inheritedCssVars.value) captureAccentColor();
    } else if (newStep <= 1 && oldStep > 1) {
        document.body.style.overflow = '';
    }

    if (trackingReady.value) {
        trackStepView();
    }
});

watch(isFs, (active) => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('wm-funnel-fs-open', !!active);
}, { immediate: true });

onUnmounted(() => {
    if (typeof document !== 'undefined') document.body.classList.remove('wm-funnel-fs-open');
});
</script>

<template>
    <div v-if="compact && step === 1" class="text-center">
        <label for="compact-plz" style="font-size: 14px; color: rgba(255,255,255,0.7); margin-bottom: 12px; display: block;">PLZ eingeben und Anfrage starten</label>
        <form @submit.prevent="nextStep" class="flex items-center mx-auto" style="background: white; border-radius: 28px; padding: 4px 4px 4px 20px; max-width: 340px;">
            <input id="compact-plz" v-model="form.postal_code" type="text" name="postal_code" inputmode="numeric" autocomplete="postal-code" autocapitalize="off" spellcheck="false" placeholder="PLZ eingeben" maxlength="5" :aria-describedby="plzError ? 'compact-plz-error' : undefined" :aria-invalid="!!plzError" style="flex: 1; border: none; outline: none; font-size: 18px; color: #333; background: transparent; min-width: 0; min-height: 44px;" class="focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1 transition-shadow duration-200" required />
            <button type="submit" aria-label="PLZ suchen" class="flex items-center justify-center shrink-0 hover:opacity-90 hover:scale-105 active:scale-[0.98] transition-all duration-150" style="width: 48px; height: 48px; background: var(--brand-accent); border-radius: 50%; border: none; color: white; cursor: pointer;">
                <MagnifyingGlassIcon class="w-5 h-5" />
            </button>
        </form>
        <p v-if="plzError" id="compact-plz-error" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; margin-top: 8px;">{{ plzError }}</p>
    </div>

    <div
        ref="shellRef"
        v-if="!(compact && step === 1)"
        :key="isFs ? 'fs' : 'inline'"
        :class="isFs ? 'funnel-fs' : 'w-full min-w-0 relative overflow-hidden funnel-shell ' + (bare && step <= 1 ? '' : 'bg-white mx-auto')"
        :style="isFs
            ? (inheritedCssVars + ';position:fixed;inset:0;z-index:9999;background:var(--brand-accent, #1A1A17);display:flex;align-items:flex-start;justify-content:center;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:20px 16px 40px;padding-top:max(20px, env(safe-area-inset-top, 0px));padding-bottom:max(40px, env(safe-area-inset-bottom, 0px));')
            : (bare ? '' : 'border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.1); border: 1px solid #eee;')"
        @click="isFs ? onOverlayClick($event) : null"
    >
        <button
            v-if="isFs"
            type="button"
            @click="closeFullscreen"
            class="fs-close"
            aria-label="Schließen"
        >
            <XMarkIcon class="w-5 h-5" />
        </button>

        <div :class="isFs ? 'fs-card' : 'w-full min-w-0'" class="funnel-inner" :style="isFs ? 'position:relative;z-index:1;width:100%;max-width:680px;margin:auto;padding:40px 36px 36px;background:#FFFFFF;border-radius:12px;box-shadow:0 20px 60px rgba(0,0,0,0.4);' : ''">

        <!-- --- Unified compact step + progress (identical for inline and fullscreen) --- -->
        <div class="fs-progress-row" role="progressbar" :aria-valuenow="step" :aria-valuemin="1" :aria-valuemax="totalSteps" :aria-label="`Schritt ${step} von ${totalSteps}`">
            <span class="fs-step-label" aria-live="polite">Schritt {{ step }} von {{ totalSteps }}</span>
            <div class="fs-progress-track">
                <div class="fs-progress-fill" :style="{ width: `${progress}%` }"></div>
            </div>
            <span v-if="encouragementText" class="fs-encourage">{{ encouragementText }}</span>
        </div>

        <form @submit.prevent="submit" class="fs-form-area">
            <div :key="currentStep.key">

            <div v-if="currentStep.key === 'property_filter'" class="space-y-5">
                <div class="text-center">
                    <div :style="{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.18em', textTransform: 'uppercase', color: c.eyebrow }">Nur für Hausbesitzer</div>
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading, marginTop: '8px', lineHeight: '1.25' }">Welche Immobilie besitzen Sie?</h2>
                </div>

                <div v-if="!disqualified" class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button type="button" @click="form.property_type = 'haus'; disqualified = false; nextStep()" class="p-4 text-center min-h-[92px] transition-[border-color,background-color]" :style="{ border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <HomeIcon class="w-7 h-7 mx-auto mb-2" :style="{ color: c.eyebrow }" />
                        <div :style="{ fontSize: '12px', fontWeight: '600', color: c.heading, lineHeight: '1.3', wordBreak: 'break-word', hyphens: 'auto' }">Einfamilienhaus</div>
                    </button>
                    <button type="button" @click="form.property_type = 'mehrfamilienhaus'; disqualified = false; nextStep()" class="p-4 text-center min-h-[92px] transition-[border-color,background-color]" :style="{ border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <BuildingOffice2Icon class="w-7 h-7 mx-auto mb-2" :style="{ color: c.eyebrow }" />
                        <div :style="{ fontSize: '12px', fontWeight: '600', color: c.heading, lineHeight: '1.3', wordBreak: 'break-word', hyphens: 'auto' }">Mehrfamilien&shy;haus</div>
                    </button>
                    <button type="button" @click="form.property_type = 'gewerbe'; disqualified = false; nextStep()" class="p-4 text-center min-h-[92px] transition-[border-color,background-color]" :style="{ border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <BuildingOffice2Icon class="w-7 h-7 mx-auto mb-2" :style="{ color: c.eyebrow }" />
                        <div :style="{ fontSize: '12px', fontWeight: '600', color: c.heading, lineHeight: '1.3' }">Gewerbe</div>
                    </button>
                </div>

                <button type="button" @click="disqualified = true" class="w-full flex items-center justify-center py-2 min-h-[44px] transition-colors hover:opacity-70" :style="{ fontSize: '12px', color: c.muted }">
                    Ich bin Mieter oder besitze keine Immobilie
                </button>

                <button v-if="isFs" type="button" @click="closeFullscreen" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Schließen
                </button>

                <div v-if="disqualified" class="p-5" :style="{ background: c.privacyBg, border: `1px solid ${c.privacyBorder}`, borderRadius: '12px', textAlign: 'center' }">
                    <h3 :style="{ fontSize: '15px', fontWeight: '700', color: c.heading, marginBottom: '6px' }">Leider können wir Ihnen nicht helfen</h3>
                    <p :style="{ fontSize: '13px', color: c.muted, lineHeight: '1.6' }">Unser Angebot richtet sich ausschließlich an Eigentümer von Immobilien in Deutschland. Schauen Sie gerne in unserem Ratgeber vorbei.</p>
                    <button type="button" @click="disqualified = false" class="mt-3 inline-flex items-center text-[12px]" :style="{ color: 'var(--brand-accent)' }">Zurück zur Auswahl</button>
                </div>
            </div>

            <div v-if="currentStep.key === 'location'" class="space-y-5">
                <div class="text-center">
                    <div :style="{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--brand-accent)' }">Ihre Region</div>
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading, marginTop: '8px' }"><label for="funnel-plz">Wo steht Ihre Immobilie?</label></h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '6px' }">Wir finden Fachbetriebe direkt aus Ihrer Region.</p>
                </div>

                <div class="relative">
                    <input id="funnel-plz" ref="plzInputRef" v-model="form.postal_code" type="text" name="postal_code" inputmode="numeric" autocomplete="postal-code" autocapitalize="off" spellcheck="false" placeholder="z.B. 10115" maxlength="5" :aria-describedby="plzError ? 'funnel-plz-error' : undefined" :aria-invalid="!!plzError" :style="{ width: '100%', padding: '16px 20px', fontSize: '22px', fontWeight: '700', minHeight: '56px', border: `2px solid ${plzError ? '#d32f2f' : c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" required />
                    <p v-if="plzError" id="funnel-plz-error" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; margin-top: 6px;">{{ plzError }}</p>
                </div>

                <button @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Beste Angebote finden
                    <ArrowRightIcon class="w-5 h-5" />
                </button>
                <ConsentNotice :brand="props.domain?.name" :color="c.muted" />

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    {{ step > 1 ? 'Zurück' : 'Schließen' }}
                </button>
            </div>

            <div v-if="currentStep.key === 'service_selection'" class="space-y-5">
                <div class="text-center">
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading }">Wofür interessieren Sie sich?</h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '4px' }">Mehrfachauswahl möglich.</p>
                </div>

                <div class="grid grid-cols-2 gap-3 sm:gap-4">
                    <button type="button" @click="toggleService('pv')" class="p-4 sm:p-6 text-center min-h-[80px] transition-[border-color,background-color,color]" :style="hasService('pv') ? 'border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px;' : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <SunIcon class="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" :style="{ color: c.eyebrow }" />
                        <span :style="{ fontSize: '14px', fontWeight: '600', color: c.heading }">Photovoltaik</span>
                    </button>
                    <button type="button" @click="toggleService('wp')" class="p-4 sm:p-6 text-center min-h-[80px] transition-[border-color,background-color,color]" :style="hasService('wp') ? 'border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px;' : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <FireIcon class="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" :style="{ color: c.eyebrow }" />
                        <span :style="{ fontSize: '14px', fontWeight: '600', color: c.heading }">Wärmepumpe</span>
                    </button>
                </div>

                <button @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Weiter
                    <ArrowRightIcon class="w-5 h-5" />
                </button>
                <ConsentNotice :brand="props.domain?.name" :color="c.muted" />

                <p v-if="stepError" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; text-align: center;">{{ stepError }}</p>

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    {{ step > 1 ? 'Zurück' : 'Schließen' }}
                </button>
            </div>

            <div v-if="currentStep.key === 'property'" class="space-y-5">
                <div class="text-center">
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading }">{{ form.first_name ? `${form.first_name}, um welchen` : 'Welchen' }} Immobilientyp handelt es sich?</h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '4px' }">Haus oder Wohnung auswählen.</p>
                </div>

                <div class="grid grid-cols-2 gap-3 sm:gap-4">
                    <button type="button" @click="form.property_type = 'haus'; nextStep()" class="p-4 sm:p-6 text-center min-h-[80px] transition-[border-color,background-color,color]" :style="form.property_type === 'haus' ? 'border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px;' : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <HomeIcon class="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" :style="{ color: c.eyebrow }" />
                        <span :style="{ fontSize: '14px', fontWeight: '600', color: c.heading }">Haus</span>
                    </button>
                    <button type="button" @click="form.property_type = 'wohnung'; nextStep()" class="p-4 sm:p-6 text-center min-h-[80px] transition-[border-color,background-color,color]" :style="form.property_type === 'wohnung' ? 'border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px;' : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px' }">
                        <BuildingOffice2Icon class="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3" :style="{ color: c.eyebrow }" />
                        <span :style="{ fontSize: '14px', fontWeight: '600', color: c.heading }">Wohnung</span>
                    </button>
                </div>

                <p v-if="stepError" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; text-align: center;">{{ stepError }}</p>

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>

            <div v-if="currentStep.key === 'qualification'" class="space-y-5">
                <div class="text-center">
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading }">{{ form.first_name ? `${form.first_name}, helfen` : 'Helfen' }} Sie uns Ihr Projekt einzuordnen</h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '4px' }">Mit diesen Angaben werden Rückmeldungen passender und schneller.</p>
                </div>

                <div class="space-y-4">
                    <div>
                        <div :style="{ fontSize: '13px', fontWeight: '600', color: c.label, marginBottom: '10px' }">Wie ist Ihre Situation?</div>
                        <div class="grid grid-cols-3 gap-2 sm:gap-3">
                            <button type="button" @click="form.ownership_status = 'owner'" class="px-2 sm:px-4 py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.ownership_status === 'owner' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Eigentümer</button>
                            <button type="button" @click="form.ownership_status = 'tenant'" class="px-2 sm:px-4 py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.ownership_status === 'tenant' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Mieter</button>
                            <button type="button" @click="form.ownership_status = 'other'" class="px-2 sm:px-4 py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.ownership_status === 'other' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Sonstiges</button>
                        </div>
                    </div>

                    <div>
                        <div :style="{ fontSize: '13px', fontWeight: '600', color: c.label, marginBottom: '10px' }">Wann möchten Sie starten?</div>
                        <div class="grid grid-cols-2 gap-2 sm:gap-3">
                            <button type="button" @click="form.installation_timeline = 'asap'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.installation_timeline === 'asap' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Sofort</button>
                            <button type="button" @click="form.installation_timeline = 'three_months'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.installation_timeline === 'three_months' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">In 3 Monaten</button>
                            <button type="button" @click="form.installation_timeline = 'six_months'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.installation_timeline === 'six_months' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">In 6 Monaten</button>
                            <button type="button" @click="form.installation_timeline = 'researching'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.installation_timeline === 'researching' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Nur informieren</button>
                        </div>
                    </div>

                    <div>
                        <div :style="{ fontSize: '13px', fontWeight: '600', color: c.label, marginBottom: '10px' }">Was ist Ihnen am wichtigsten?</div>
                        <div class="grid grid-cols-2 gap-2 sm:gap-3">
                            <button type="button" @click="form.project_goal = 'savings'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.project_goal === 'savings' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Kosten sparen</button>
                            <button type="button" @click="form.project_goal = 'independence'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.project_goal === 'independence' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Unabhängigkeit</button>
                            <button type="button" @click="form.project_goal = 'modernization'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.project_goal === 'modernization' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Modernisierung</button>
                            <button type="button" @click="form.project_goal = 'comparison'" class="px-3 sm:px-4 py-3 sm:py-4 min-h-[48px] text-sm sm:text-base transition-[border-color,background-color,color]" :style="form.project_goal === 'comparison' ? `border: 2px solid var(--brand-accent); background: var(--brand-accent-soft); border-radius: 12px; color: ${c.heading};` : { border: `2px solid ${c.inputBorder}`, background: c.inputBg, borderRadius: '12px', color: c.optUnselected }">Angebote vergleichen</button>
                        </div>
                    </div>
                </div>

                <p v-if="stepError" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; text-align: center;">{{ stepError }}</p>

                <button v-if="form.ownership_status && form.installation_timeline && form.project_goal" @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Weiter
                    <ArrowRightIcon class="w-5 h-5" />
                </button>
                <ConsentNotice v-if="form.ownership_status && form.installation_timeline && form.project_goal" :brand="props.domain?.name" :color="c.muted" />

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>

            <div v-if="currentStep.key === 'costs'" class="space-y-5">
                <div class="text-center">
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading }">{{ form.first_name ? `${form.first_name}, wie` : 'Wie' }} hoch sind Ihre monatlichen Kosten?</h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '4px' }">Ein grober Richtwert reicht aus.</p>
                </div>

                <div class="py-8 text-center">
                    <div style="font-size: 56px; font-weight: 700; color: var(--brand-accent-fg, var(--brand-accent)); letter-spacing: -2px;" class="tabular-nums">{{ form.monthly_bill }}<span style="font-size: 24px; font-weight: 400; margin-left: 2px;">&euro;</span></div>
                    <label for="funnel-bill-range" class="sr-only">Monatliche Energiekosten</label>
                    <input id="funnel-bill-range" type="range" v-model="form.monthly_bill" min="50" max="600" step="10" class="w-full mt-4" style="accent-color: var(--brand-accent-fg, var(--brand-accent)); min-height: 44px;" />
                    <div class="flex justify-between mt-1" :style="{ fontSize: '12px', color: c.muted }">
                        <span>50&euro;</span>
                        <span>600&euro;</span>
                    </div>
                    <div :style="{ marginTop: '10px', padding: '8px 14px', background: c.savingsBg, border: `1px solid ${c.savingsBorder}`, borderRadius: '10px', fontSize: '12px', color: c.savingsColor, textAlign: 'center' }">
                        Geschätztes Einsparpotential: ca. <strong>{{ estimatedSavings }}&euro; pro Jahr</strong>
                    </div>
                </div>

                <button @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Weiter zum letzten Schritt
                    <ArrowRightIcon class="w-5 h-5" />
                </button>
                <ConsentNotice :brand="props.domain?.name" :color="c.muted" />

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>

            <div v-if="currentStep.key === 'phone'" class="space-y-5">
                <div class="text-center">
                    <div :style="{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--brand-accent)' }">Letzter Schritt</div>
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading, marginTop: '8px' }">Ihr Ergebnis ist fertig</h2>
                    <p id="funnel-phone-hint" :style="{ fontSize: '13px', color: c.sub, marginTop: '6px' }">Wohin dürfen wir Ihre besten Angebote senden? Ein kurzer Rückruf genügt — nur Ihre Telefonnummer, kein Spam.</p>
                </div>

                <div :style="{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: c.urgentBg, border: `1px solid ${c.urgentBorder}`, borderRadius: '10px' }">
                    <ClockIconMini class="w-4 h-4 shrink-0" :style="{ color: c.urgentIcon }" />
                    <span :style="{ fontSize: '12px', color: c.urgentColor, fontWeight: '500' }">Heute bereits <strong>{{ todayLeadCount || (isWP ? 14 : 11) }}+ Anfragen</strong> in Ihrer Region eingegangen.</span>
                </div>

                <!-- Final step is PHONE-ONLY (2026-05-30): the 3-field
                     Vorname+Nachname+Telefon wall at peak commitment was the
                     highest-friction screen. Name is now collected optionally on
                     the Success page via the /leads/{id}/enrich endpoint. Phone is
                     the single required money field. The result-reveal panel below
                     is the value gate that pulls step-advancers across the ask. -->
                <div :style="{ padding: '12px 16px', background: c.savingsBg, border: `1px solid ${c.savingsBorder}`, borderRadius: '12px' }">
                    <strong :style="{ color: c.savingsColor, fontSize: '13px' }">Ihre passenden Fachbetriebe stehen bereit.</strong>
                    <span :style="{ color: c.savingsColor, fontSize: '13px' }"> Geben Sie Ihre Telefonnummer ein - wir melden uns mit Ihren besten Angeboten.</span>
                </div>

                <div>
                    <label for="funnel-phone" :style="{ fontSize: '13px', fontWeight: '600', color: c.label, display: 'block', marginBottom: '4px' }">Telefon</label>
                    <input id="funnel-phone" ref="phoneRef" v-model="form.phone" type="tel" autocomplete="tel" inputmode="tel" placeholder="z.B. 0151 12345678" :aria-describedby="phoneError ? 'funnel-phone-error' : 'funnel-phone-hint'" :aria-invalid="!!phoneError" :style="{ width: '100%', padding: '16px 20px', fontSize: '22px', fontWeight: '700', minHeight: '56px', border: `2px solid ${phoneError ? '#d32f2f' : c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" required />
                    <p v-if="phoneError" id="funnel-phone-error" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; margin-top: 6px;">{{ phoneError }}</p>
                </div>

                <p v-if="(stepError && !phoneError) || submitError" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; text-align: center;">{{ submitError || stepError }}</p>

                <!-- privacyRef kept for focus/scroll-into-view on submit errors -->
                <div ref="privacyRef" style="display: contents;"></div>

                <!-- UWG § 7 SEPARATE, UNticked phone-advertising opt-in. Submit
                     stays enabled regardless; this only governs the right to
                     call/SMS. LEGAL: wording mirrors config('consent.phone_optin_checkbox')
                     and requires Fachanwalt sign-off before go-live. -->
                <label :style="{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '12px 14px', background: c.privacyBg, border: `1px solid ${c.privacyBorder}`, borderRadius: '10px', cursor: 'pointer' }">
                    <input type="checkbox" v-model="form.phone_advertising_consent" style="margin-top: 3px; width: 18px; height: 18px; flex-shrink: 0; accent-color: var(--brand-accent);" />
                    <span :style="{ fontSize: '12px', lineHeight: '1.45', color: c.privacyColor }">
                        Ja, {{ props.domain?.name || 'wir' }} und der jeweils vermittelte Fachbetrieb dürfen mich unter der angegebenen Rufnummer telefonisch und per SMS zu meinem kostenlosen Angebot kontaktieren. Freiwillig, jederzeit formfrei widerrufbar.
                    </span>
                </label>

                <button type="submit" :disabled="form.processing" :class="{ 'cta-pulse': !form.processing }" class="cta-btn-primary w-full flex items-center justify-center gap-2" :style="form.processing ? 'padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: #cbd5e1; cursor: not-allowed; opacity: 0.7; min-height: 56px;' : 'padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;'" :aria-busy="form.processing">
                    <svg v-if="form.processing" class="cta-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="31.4 31.4" /></svg>
                    {{ form.processing ? 'Wird gesendet...' : 'Jetzt beste Angebote erhalten' }}
                    <ArrowRightIcon v-if="!form.processing" class="w-5 h-5" />
                </button>
                <ConsentNotice :brand="props.domain?.name" :color="c.muted" />

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>

            <div v-if="currentStep.key === 'name'" class="space-y-5">
                <div class="text-center">
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading }">Wie heißen Sie?</h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '4px' }">Für eine persönliche Angebotserstellung.</p>
                </div>

                <div class="space-y-3">
                    <div>
                        <label for="funnel-firstname" :style="{ fontSize: '13px', fontWeight: '600', color: c.label, display: 'block', marginBottom: '4px' }">Vorname</label>
                        <input id="funnel-firstname" ref="firstNameRef" v-model="form.first_name" placeholder="Vorname" autocomplete="given-name" :aria-describedby="nameError ? 'funnel-name-error' : undefined" :aria-invalid="!!nameError && !form.first_name?.trim()" :style="{ width: '100%', padding: '14px 18px', fontSize: '16px', minHeight: '48px', border: `2px solid ${nameError && !form.first_name?.trim() ? '#d32f2f' : c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" required />
                    </div>
                    <div>
                        <label for="funnel-lastname" :style="{ fontSize: '13px', fontWeight: '600', color: c.label, display: 'block', marginBottom: '4px' }">Nachname</label>
                        <input id="funnel-lastname" v-model="form.last_name" placeholder="Nachname" autocomplete="family-name" :aria-describedby="nameError ? 'funnel-name-error' : undefined" :aria-invalid="!!nameError && !form.last_name?.trim()" :style="{ width: '100%', padding: '14px 18px', fontSize: '16px', minHeight: '48px', border: `2px solid ${nameError && !form.last_name?.trim() ? '#d32f2f' : c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" required />
                    </div>
                    <p v-if="nameError" id="funnel-name-error" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f;">{{ nameError }}</p>
                </div>

                <button @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Weiter
                    <ArrowRightIcon class="w-5 h-5" />
                </button>
                <ConsentNotice :brand="props.domain?.name" :color="c.muted" />

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>

            <div v-if="currentStep.key === 'address'" class="space-y-5">
                <div class="text-center">
                    <div :style="{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--brand-accent)' }">Optional</div>
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading, marginTop: '8px' }">Adresse des Objekts</h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '6px' }">Für ein präziseres Angebot — Sie können diesen Schritt auch überspringen.</p>
                </div>

                <div class="grid grid-cols-3 gap-3">
                    <div class="col-span-2">
                        <label for="funnel-street" :style="{ fontSize: '13px', fontWeight: '600', color: c.label, display: 'block', marginBottom: '4px' }">Straße</label>
                        <input id="funnel-street" ref="streetRef" v-model="form.street" placeholder="Straße" autocomplete="street-address" :style="{ width: '100%', padding: '14px 18px', fontSize: '16px', minHeight: '48px', border: `2px solid ${c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" />
                    </div>
                    <div>
                        <label for="funnel-housenr" :style="{ fontSize: '13px', fontWeight: '600', color: c.label, display: 'block', marginBottom: '4px' }">Nr.</label>
                        <input id="funnel-housenr" v-model="form.house_number" placeholder="Nr." :style="{ width: '100%', padding: '14px 18px', fontSize: '16px', minHeight: '48px', border: `2px solid ${c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" />
                    </div>
                </div>

                <p v-if="stepError" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; text-align: center;">{{ stepError }}</p>

                <button @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Weiter
                    <ArrowRightIcon class="w-5 h-5" />
                </button>

                <button type="button" @click="skipOptional" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', fontWeight: '600', color: c.muted, textDecoration: 'underline' }">
                    Weiter ohne Angabe
                </button>

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>

            <div v-if="currentStep.key === 'email'" class="space-y-5">
                <div class="text-center">
                    <div :style="{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--brand-accent)' }">Optional</div>
                    <h2 :style="{ fontSize: '22px', fontWeight: '700', color: c.heading, marginTop: '8px' }"><label for="funnel-email">E-Mail-Adresse</label></h2>
                    <p :style="{ fontSize: '13px', color: c.sub, marginTop: '6px' }">Für die schriftliche Übersicht der Angebote — Sie können diesen Schritt auch überspringen.</p>
                </div>

                <div>
                    <input id="funnel-email" ref="emailRef" v-model="form.email" type="email" autocomplete="email" placeholder="ihre@email.de" :aria-describedby="emailError ? 'funnel-email-error' : undefined" :aria-invalid="!!emailError" :style="{ width: '100%', padding: '14px 18px', fontSize: '16px', minHeight: '48px', border: `2px solid ${emailError ? '#d32f2f' : c.inputBorder}`, borderRadius: '12px', outline: 'none', background: c.inputBg, color: c.inputColor, transition: 'border-color 0.2s, box-shadow 0.2s' }" class="focus:!border-[var(--brand-accent)] focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] focus-visible:ring-offset-1" />
                    <p v-if="emailError" id="funnel-email-error" aria-live="polite" role="alert" style="font-size: 12px; color: #d32f2f; margin-top: 6px;">{{ emailError }}</p>
                </div>

                <button @click="nextStep" type="button" class="cta-btn-primary w-full flex items-center justify-center gap-2" style="padding: 16px; font-size: 16px; font-weight: 700; border-radius: 24px; background: var(--brand-accent); box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-height: 56px;">
                    Weiter
                    <ArrowRightIcon class="w-5 h-5" />
                </button>

                <button type="button" @click="skipOptional" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', fontWeight: '600', color: c.muted, textDecoration: 'underline' }">
                    Weiter ohne Angabe
                </button>

                <button type="button" @click="prevStep" class="w-full flex items-center justify-center gap-1.5 py-2 min-h-[48px] transition-colors hover:opacity-70" :style="{ fontSize: '13px', color: c.muted }">
                    <ArrowLeftIcon class="w-3.5 h-3.5" />
                    Zurück
                </button>
            </div>


            </div>
        </form>
        </div>

        <div
            v-if="confirmExitOpen"
            class="fs-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fs-confirm-title"
            aria-describedby="fs-confirm-desc"
            @click.stop
        >
            <div class="fs-confirm__backdrop" @click="cancelExit"></div>
            <div class="fs-confirm__card" role="document">
                <h3 id="fs-confirm-title" class="fs-confirm__title">Sind Sie sicher?</h3>
                <p id="fs-confirm-desc" class="fs-confirm__lead">
                    Wenn Sie jetzt abbrechen, gehen Ihre bisherigen Angaben verloren. Bitte schließen Sie die Anfrage in unter 60 Sekunden ab - der Vergleich ist kostenlos und unverbindlich.
                </p>
                <div class="fs-confirm__btns">
                    <button type="button" class="fs-confirm__primary" @click="cancelExit">Anfrage weiter ausfüllen</button>
                    <button type="button" class="fs-confirm__ghost" @click="performClose">Trotzdem abbrechen</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
/* Global (non-scoped) — keep app chrome in DOM while the funnel owns the
   viewport (avoid mid-patch display:none that confuses Vue transitions),
   but drop pointer events + visibility so header CTAs can't intercept
   clicks on the funnel's close button. */
body.wm-funnel-fs-open .app-header,
body.wm-funnel-fs-open .wm-floating-cta {
    pointer-events: none !important;
    visibility: hidden !important;
}
</style>

<style scoped>
/* === Fullscreen overlay === */
.funnel-fs {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: #1A1A17;
    padding: 20px 16px 40px;
    padding-top: max(20px, env(safe-area-inset-top, 0px));
    padding-bottom: max(40px, env(safe-area-inset-bottom, 0px));
    min-height: 100vh;
    min-height: 100dvh;
    height: 100vh;
    height: 100dvh;
    /* Override the global animation killer for the fullscreen entrance */
    animation: fs-in 0.28s ease-out both !important;
}

@keyframes fs-in {
    from { opacity: 0; }
    to   { opacity: 1; }
}

/* === Centered card === */
.fs-card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 480px;
    margin: auto;
    padding: 28px 22px 24px;
    background: #FFFFFF;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    /* Override the global animation killer for the card entrance */
    animation: fs-card-in 0.32s 0.08s cubic-bezier(0.16, 1, 0.3, 1) both !important;
}

@keyframes fs-card-in {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
}

@media (min-width: 640px) {
    .fs-card {
        padding: 36px 32px 32px;
        margin: 40px auto;
    }
}

/* === Compact progress row === */
.fs-progress-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
}

.fs-step-label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
}

.fs-progress-track {
    flex: 1;
    height: 4px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
}

.fs-progress-fill {
    height: 100%;
    background: var(--brand-accent, #3b82f6);
    border-radius: 4px;
    transition: width 0.5s ease;
}

/* === Encouragement badge === */
.fs-encourage {
    font-size: 11px;
    font-weight: 600;
    color: var(--brand-accent-fg, var(--brand-accent));
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 20px;
    padding: 2px 10px;
    white-space: nowrap;
    animation: encourage-in 0.4s ease-out;
}

@keyframes encourage-in {
    from { opacity: 0; transform: scale(0.8); }
    to   { opacity: 1; transform: scale(1); }
}

/* === Form area inside card === */
.fs-form-area {
    padding: 0;
}

/* === Close button === */
.fs-close {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
}
.fs-close:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
}
.fs-close svg {
    color: inherit;
}

/* === Exit confirmation === */
.fs-confirm {
    position: fixed;
    inset: 0;
    z-index: 10001;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #1a1a1a;
}
.fs-confirm__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    animation: fs-confirm-fade 160ms ease-out;
}
.fs-confirm__card {
    position: relative;
    background: #fff;
    border-radius: 14px;
    padding: 28px 24px 22px;
    max-width: 440px;
    width: 100%;
    box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(0, 0, 0, 0.06);
    animation: fs-confirm-in 200ms cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 1;
}
.fs-confirm__title {
    font-size: 22px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 10px;
    line-height: 1.2;
}
.fs-confirm__lead {
    font-size: 14px;
    line-height: 1.55;
    color: #4b5563;
    margin: 0 0 22px;
}
.fs-confirm__btns {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.fs-confirm__primary,
.fs-confirm__ghost {
    width: 100%;
    min-height: 48px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 140ms ease, color 140ms ease, border-color 140ms ease, opacity 140ms ease;
}
.fs-confirm__primary {
    background: var(--brand-accent, #EF9209);
    color: var(--brand-accent-fg, #fff);
    border: 1px solid var(--brand-accent, #EF9209);
}
.fs-confirm__primary:hover { opacity: 0.92; }
.fs-confirm__ghost {
    background: transparent;
    color: #6b7280;
    border: 1px solid rgba(0, 0, 0, 0.12);
}
.fs-confirm__ghost:hover { background: rgba(0, 0, 0, 0.04); color: #374151; }
.fs-confirm__primary:focus-visible,
.fs-confirm__ghost:focus-visible {
    outline: 2px solid var(--brand-accent, #EF9209);
    outline-offset: 2px;
}
@keyframes fs-confirm-fade {
    from { opacity: 0; }
    to   { opacity: 1; }
}
@keyframes fs-confirm-in {
    from { opacity: 0; transform: translateY(12px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* === Blob background === */
.fs-blob-layer {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
}

.fs-blob {
    position: absolute;
    border-radius: 40% 60% 55% 45%;
    opacity: 0;
    will-change: transform, border-radius;
}

.fs-blob-1 {
    width: min(60vw, 700px);
    height: min(60vw, 700px);
    background: radial-gradient(circle, var(--brand-accent, #3b82f6) 0%, transparent 70%);
    top: -18%;
    right: -12%;
    filter: blur(60px);
    animation: blob-in 1s 0.2s ease-out forwards, blob-move-1 22s 1.2s ease-in-out infinite;
}

.fs-blob-2 {
    width: min(50vw, 600px);
    height: min(50vw, 600px);
    background: radial-gradient(circle, #a78bfa 0%, transparent 70%);
    bottom: -12%;
    left: -14%;
    filter: blur(65px);
    animation: blob-in 1s 0.4s ease-out forwards, blob-move-2 26s 1.4s ease-in-out infinite;
}

.fs-blob-3 {
    width: min(45vw, 520px);
    height: min(45vw, 520px);
    background: radial-gradient(circle, #34d399 0%, transparent 70%);
    top: 35%;
    left: 50%;
    filter: blur(55px);
    animation: blob-in 1s 0.6s ease-out forwards, blob-move-3 20s 1.6s ease-in-out infinite;
}

@keyframes blob-in {
    from { opacity: 0; transform: scale(0.3); }
    to   { opacity: 0.18; transform: scale(1); }
}

@keyframes blob-move-1 {
    0%, 100% { transform: translate(0, 0) scale(1); border-radius: 40% 60% 55% 45%; }
    25% { transform: translate(-50px, 35px) scale(1.08); border-radius: 55% 45% 60% 40%; }
    50% { transform: translate(20px, -40px) scale(0.94); border-radius: 45% 55% 40% 60%; }
    75% { transform: translate(-30px, -20px) scale(1.06); border-radius: 60% 40% 50% 50%; }
}

@keyframes blob-move-2 {
    0%, 100% { transform: translate(0, 0) scale(1); border-radius: 55% 45% 50% 50%; }
    25% { transform: translate(45px, -50px) scale(1.1); border-radius: 45% 55% 60% 40%; }
    50% { transform: translate(-35px, 20px) scale(0.9); border-radius: 60% 40% 45% 55%; }
    75% { transform: translate(25px, 45px) scale(1.05); border-radius: 50% 50% 40% 60%; }
}

@keyframes blob-move-3 {
    0%, 100% { transform: translate(0, 0) scale(1); border-radius: 50% 50% 45% 55%; }
    25% { transform: translate(-40px, -30px) scale(1.07); border-radius: 60% 40% 55% 45%; }
    50% { transform: translate(50px, 20px) scale(0.88); border-radius: 40% 60% 50% 50%; }
    75% { transform: translate(-20px, 40px) scale(1.1); border-radius: 55% 45% 40% 60%; }
}

/* === Step transitions === */
.step-fwd-enter-active,
.step-fwd-leave-active,
.step-back-enter-active,
.step-back-leave-active {
    transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}

.step-fwd-enter-from { opacity: 0; transform: translateX(40px); }
.step-fwd-leave-to   { opacity: 0; transform: translateX(-40px); }
.step-back-enter-from { opacity: 0; transform: translateX(-40px); }
.step-back-leave-to   { opacity: 0; transform: translateX(40px); }

/* === Scrollbar hide utility === */
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

/* === CTA pulse animation === */
.cta-pulse {
    animation: cta-pulse 2s ease-in-out infinite;
}

@keyframes cta-pulse {
    0%, 100% { box-shadow: 0 8px 24px rgba(0,0,0,0.15); transform: scale(1); }
    50%      { box-shadow: 0 8px 32px rgba(0,0,0,0.25), 0 0 0 8px rgba(var(--brand-accent-rgb, 59,130,246), 0.15); transform: scale(1.02); }
}

.cta-pulse:hover {
    animation: none;
}

/* === Primary button states === */
.cta-btn-primary {
    transition: transform 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease;
    cursor: pointer;
    border: none;
}

.cta-btn-primary:hover {
    opacity: 0.9;
    transform: scale(1.015);
    filter: brightness(0.95);
}

.cta-btn-primary:active {
    transform: scale(0.98);
}

.cta-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    filter: none;
}

.cta-btn-primary:focus-visible {
    outline: 2px solid var(--brand-accent, #3b82f6);
    outline-offset: 2px;
}

/* === Loading spinner === */
.cta-spinner {
    animation: cta-spin 0.8s linear infinite;
}

@keyframes cta-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
}

/* ============================================================
   TRUST & AUTHORITY UI IMPROVEMENTS
   ============================================================ */

/* --- Text inputs --- */
.funnel-fs input[type="text"],
.funnel-fs input[type="email"],
.funnel-fs input[type="tel"],
.funnel-fs input[type="number"] {
    min-height: 48px;
    padding: 12px 16px;
    border-width: 1.5px;
    border-style: solid;
    transition: border-color 200ms ease-out, box-shadow 200ms ease-out, background-color 200ms ease-out;
}

.funnel-fs input[type="text"]:focus,
.funnel-fs input[type="email"]:focus,
.funnel-fs input[type="tel"]:focus,
.funnel-fs input[type="number"]:focus {
    outline: none;
    border-color: var(--brand-accent, #2563eb) !important;
    box-shadow: 0 0 0 3px var(--brand-accent-soft, rgba(37, 99, 235, 0.15));
}

.funnel-fs input[type="text"]:focus-visible,
.funnel-fs input[type="email"]:focus-visible,
.funnel-fs input[type="tel"]:focus-visible,
.funnel-fs input[type="number"]:focus-visible {
    outline: 3px solid var(--brand-accent, #2563eb);
    outline-offset: 2px;
    border-color: var(--brand-accent, #2563eb) !important;
    box-shadow: 0 0 0 3px var(--brand-accent-soft, rgba(37, 99, 235, 0.15));
}

/* Dark mode: ensure readable text/borders */
.funnel-fs input[type="text"]::placeholder,
.funnel-fs input[type="email"]::placeholder,
.funnel-fs input[type="tel"]::placeholder,
.funnel-fs input[type="number"]::placeholder {
    opacity: 0.55;
}

/* --- Radio / checkbox option buttons --- */
.fs-option-btn {
    min-height: 48px;
    border: 1.5px solid var(--fs-opt-border, #e2e8f0);
    border-radius: 12px;
    padding: 10px 14px;
    transition: border-color 200ms ease-out, background-color 200ms ease-out, box-shadow 200ms ease-out;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    background: transparent;
}

.fs-option-btn:hover {
    border-color: var(--brand-accent, #2563eb);
    box-shadow: 0 0 0 1px var(--brand-accent-soft, rgba(37, 99, 235, 0.15));
}

.fs-option-btn:focus-visible {
    outline: 3px solid var(--brand-accent, #2563eb);
    outline-offset: 2px;
}

.fs-option-btn.is-selected {
    border-color: var(--brand-accent, #2563eb);
    background-color: var(--brand-accent-soft, rgba(37, 99, 235, 0.08));
    box-shadow: 0 0 0 1px var(--brand-accent, #2563eb);
}

/* --- Progress bar --- */
.fs-progress-fill {
    transition: width 200ms ease-out !important;
}

/* --- Next / primary action buttons --- */
.cta-btn-primary {
    min-height: 52px !important;
    background: var(--brand-accent, #2563eb);
    transition: transform 200ms ease-out, box-shadow 200ms ease-out, opacity 200ms ease-out !important;
}

.cta-btn-primary:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 12px 32px rgba(37, 99, 235, 0.35), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    opacity: 1 !important;
    filter: none !important;
}

.cta-btn-primary:active:not(:disabled) {
    transform: translateY(0) scale(0.99) !important;
}

.cta-btn-primary:focus-visible {
    outline: 3px solid var(--brand-accent, #2563eb);
    outline-offset: 2px;
}

/* --- Back / secondary buttons --- */
.fs-back-btn {
    min-height: 44px;
    border: 1.5px solid var(--fs-back-border, #e2e8f0);
    border-radius: 24px;
    background: transparent;
    color: #64748b;
    padding: 8px 20px;
    font-size: 13px;
    transition: border-color 200ms ease-out, color 200ms ease-out, background-color 200ms ease-out;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.fs-back-btn:hover {
    border-color: #94a3b8;
    color: #334155;
    background-color: rgba(148, 163, 184, 0.08);
}

.fs-back-btn:focus-visible {
    outline: 3px solid var(--brand-accent, #2563eb);
    outline-offset: 2px;
}

/* --- Error messages --- */
.fs-error-msg {
    color: #dc2626;
    font-size: 13px;
    line-height: 1.5;
    margin-top: 6px;
    display: block;
}

/* --- Step transitions (tightened to 200ms) --- */
.step-fwd-enter-active,
.step-fwd-leave-active,
.step-back-enter-active,
.step-back-leave-active {
    transition: opacity 200ms ease-out, transform 200ms ease-out !important;
}

/* --- Focus-visible: global rule for interactive elements in funnel --- */
.funnel-fs button:focus-visible,
.funnel-fs a:focus-visible,
.funnel-fs label:focus-visible {
    outline: 3px solid var(--brand-accent, #2563eb);
    outline-offset: 2px;
}

/* --- Dark-mode input contrast (step > 1 forces light card; step 1 on dark bg) --- */
@media (prefers-color-scheme: dark) {
    .funnel-fs input[type="text"],
    .funnel-fs input[type="email"],
    .funnel-fs input[type="tel"],
    .funnel-fs input[type="number"] {
        color: #f1f5f9;
        border-color: rgba(255, 255, 255, 0.18);
        background-color: rgba(255, 255, 255, 0.07);
    }
    .funnel-fs input[type="text"]:focus,
    .funnel-fs input[type="email"]:focus,
    .funnel-fs input[type="tel"]:focus,
    .funnel-fs input[type="number"]:focus {
        border-color: var(--brand-accent, #3b82f6) !important;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
    }
}

</style>
